/**
 * Auth API Service
 * 
 * API calls for authentication
 */

import { api, type ApiResponse, ApiError } from './api';

export interface LoginCredentials {
    email: string;
    password: string;
    otp?: string;
}

export interface LoginResponse {
    access_token: string;
    refresh_token: string;
    expires: number;
}

export interface User {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    status: string;
    role: string | null;
    avatar: string | null;
    language: string | null;
    theme_dark: string | null;
    theme_light: string | null;
    appearance: string | null;
    title: string | null;
    description: string | null;
    location: string | null;
    last_access: string | null;
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/login', credentials, {
        skipAuth: true,
    });

    return response.data;
}

/**
 * Refresh access token
 */
export async function refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
        '/auth/refresh',
        { refresh_token: refreshToken },
        { skipAuth: true }
    );

    return response.data;
}

/**
 * Logout
 */
export async function logout(refreshToken?: string): Promise<void> {
    try {
        await api.post('/auth/logout', { refresh_token: refreshToken });
    } catch {
        // Ignore logout errors - we're logging out anyway
    }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/users/me');
    return response.data;
}

/**
 * Update current user
 */
export async function updateCurrentUser(data: Partial<User>): Promise<User> {
    const response = await api.patch<ApiResponse<User>>('/users/me', data);
    return response.data;
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/password/request', { email }, { skipAuth: true });
}

/**
 * Reset password with token
 */
export async function resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/password/reset', { token, password }, { skipAuth: true });
}

/**
 * Policy Globals - access flags for the current user
 * Mirrors reference: /policies/me/globals
 */
export interface PolicyGlobals {
    app_access: boolean;
    admin_access: boolean;
    enforce_tfa: boolean;
}

/**
 * Get policy globals for current user
 */
export async function getPolicyGlobals(): Promise<PolicyGlobals> {
    const response = await api.get<ApiResponse<PolicyGlobals>>('/policies/me/globals');
    return response.data;
}

export { ApiError };
